import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import axios from "axios";
import { useSession } from "@/services/auth";
import * as z from "zod";
import { useTranslation } from "react-i18next";
import {
  Avatar,
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@heroui/react";
import { isValidPhoneNumber } from "react-phone-number-input";
import type { Customer } from "@/api.types";
import { HeroFormField, HeroInputField } from "@/components/hero-form";
import { PhoneInputShadcn } from "@/components/ui/phone-input-shadcn";

type ProfileFormValues = {
  firstName: string;
  lastName: string;
  preferredName?: string;
  email: string;
  phone: string;
};

export default function ProfileScreen() {
  const { t } = useTranslation("app");
  const { token, customer } = useSession();
  const queryClient = useQueryClient();
  const [isEditOpen, setIsEditOpen] = useState(false);

  const profileSchema = z.object({
    firstName: z.string().min(1, t("validation.firstNameRequired")),
    lastName: z.string().min(1, t("validation.lastNameRequired")),
    preferredName: z.string().optional(),
    email: z.string().email(t("validation.emailInvalid")),
    phone: z
      .string()
      .refine(isValidPhoneNumber, { message: t("validation.phoneInvalid") }),
  });

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      preferredName: "",
      email: "",
      phone: "",
    },
    values: customer
      ? {
          firstName: customer?.firstName ?? "",
          lastName: customer?.lastName ?? "",
          preferredName: "",
          email: customer?.email ?? "",
          phone: customer?.phone ?? "",
        }
      : undefined,
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      if (!customer?.id) throw new Error("No customer ID found");
      const { data } = await axios.patch<Customer>(
        `${import.meta.env.VITE_API_URL}/api/v2/customers/${customer?.id}`,
        values,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      return data;
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ["customer", customer?.id],
      });
      setIsEditOpen(false);
    },
  });

  const onSubmit = form.handleSubmit((values) => {
    updateProfileMutation.mutate(values);
  });

  const initials = `${customer?.firstName?.[0] ?? ""}${customer?.lastName?.[0] ?? ""}`;

  return (
    <div className="w-full max-w-xl space-y-8">
      <div>
        <h2 className="text-2xl font-semibold">{t("profileScreen.title")}</h2>
        <p className="text-sm text-default-500">{t("profileScreen.lede")}</p>
      </div>

      <div className="space-y-6">
        <Avatar
          radius="md"
          size="lg"
          name={initials || undefined}
          className="h-24 w-24 text-base"
        />

        <div className="space-y-4">
          <Input
            label={t("onboarding.firstName")}
            value={customer?.firstName ?? ""}
            isDisabled
            variant="flat"
          />
          <Input
            label={t("onboarding.lastName")}
            value={customer?.lastName ?? ""}
            isDisabled
            variant="flat"
          />
          <Input
            label={t("profileScreen.preferredName")}
            value=""
            isDisabled
            variant="flat"
          />
          <Input
            label={t("settings.email")}
            value={customer?.email ?? ""}
            isDisabled
            variant="flat"
          />
          <Input
            label={t("settings.phone")}
            value={customer?.phone ?? ""}
            isDisabled
            variant="flat"
          />
        </div>

        <Button color="primary" fullWidth onPress={() => setIsEditOpen(true)}>
          {t("settings.editProfile")}
        </Button>
      </div>

      <Modal
        isOpen={isEditOpen}
        onOpenChange={setIsEditOpen}
        placement="center"
        backdrop="blur"
        disableAnimation
        size="md"
      >
        <ModalContent>
          {(onClose) => (
            <form onSubmit={onSubmit}>
              <ModalHeader className="flex-col items-start gap-1">
                <span>{t("settings.editProfile")}</span>
                <span className="text-small text-default-500 font-normal">
                  {t("profileScreen.editDescription")}
                </span>
              </ModalHeader>
              <ModalBody className="gap-4">
                <HeroInputField
                  control={form.control}
                  name="firstName"
                  label={t("onboarding.firstName")}
                  variant="flat"
                />
                <HeroInputField
                  control={form.control}
                  name="lastName"
                  label={t("onboarding.lastName")}
                  variant="flat"
                />
                <HeroInputField
                  control={form.control}
                  name="preferredName"
                  label={t("profileScreen.preferredName")}
                  variant="flat"
                />
                <HeroInputField
                  control={form.control}
                  name="email"
                  label={t("settings.email")}
                  variant="flat"
                />
                <HeroFormField
                  control={form.control}
                  name="phone"
                  render={({ field, fieldState }) => (
                    <div>
                      <label className="block text-tiny font-medium text-default-700 mb-1.5">
                        {t("settings.phone")}
                      </label>
                      <PhoneInputShadcn
                        value={field.value ?? ""}
                        onChange={(v) => field.onChange(v ?? "")}
                        onBlur={field.onBlur}
                        defaultCountry="CA"
                      />
                      {fieldState.error?.message && (
                        <p className="mt-1 text-tiny text-danger">
                          {fieldState.error.message}
                        </p>
                      )}
                    </div>
                  )}
                />
              </ModalBody>
              <ModalFooter>
                <Button variant="light" onPress={onClose}>
                  {t("actions.cancel")}
                </Button>
                <Button
                  type="submit"
                  color="primary"
                  isDisabled={updateProfileMutation.isPending}
                  isLoading={updateProfileMutation.isPending}
                >
                  {updateProfileMutation.isPending
                    ? t("actions.saving")
                    : t("actions.saveChanges")}
                </Button>
              </ModalFooter>
            </form>
          )}
        </ModalContent>
      </Modal>
    </div>
  );
}
